import { useState, type FormEvent } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Eye, EyeOff, Loader2, Lock, CheckCircle, ArrowLeft } from 'lucide-react'

import { useAuthStore } from '@/core/store/authStore'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/reset-password' as any })
  const codeFromUrl = (search as any)?.code as string | undefined

  const confirmReset = useAuthStore((s) => s.confirmReset)
  const isConfirmingReset = useAuthStore((s) => s.isConfirmingReset)
  const resetConfirmError = useAuthStore((s) => s.resetConfirmError)
  const resetConfirmSuccess = useAuthStore((s) => s.resetConfirmSuccess)

  const [code, setCode] = useState(codeFromUrl ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!code || password !== confirmPassword) {return}
    await confirmReset(code, password)
  }

  const passwordsMatch = password === confirmPassword && password.length > 0
  const canSubmit = code.length === 6 && password.length >= 8 && passwordsMatch

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-150 overflow-hidden">
          {/* Header */}
          <div className="bg-navy px-8 py-8 flex flex-col items-center gap-3">
            <img
              src="/images/logo/ub-logo-128.webp"
              alt="UBSafety"
              className="w-16 h-16 object-contain"
            />
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white tracking-tight">UBSafety</h1>
              <p className="text-slate-400 text-sm mt-1">Industrial Safety Dashboard</p>
            </div>
          </div>

          <div className="px-8 py-8">
            {!resetConfirmSuccess ? (
              <>
                <h2 className="text-lg font-semibold text-navy mb-1">Create New Password</h2>
                <p className="text-sm text-slate-500 mb-6">
                  Enter your new password below.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Reset Code */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-navy">
                      Reset Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={isConfirmingReset}
                      placeholder="6-digit code from email"
                      className="w-full h-10 px-3 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition tracking-widest text-center"
                    />
                  </div>

                  {/* New Password */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-navy">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isConfirmingReset}
                        placeholder="Min. 8 characters"
                        className="w-full h-10 pl-10 pr-10 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-navy"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {password.length > 0 && password.length < 8 && (
                      <p className="text-xs text-red-500">Password must be at least 8 characters</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-navy">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isConfirmingReset}
                        placeholder="Re-enter password"
                        className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-150 bg-white text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 transition"
                      />
                    </div>
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>

                  {resetConfirmError && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                      <p className="text-sm text-red-500">{resetConfirmError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit || isConfirmingReset}
                    className="w-full h-10 rounded-lg bg-primary text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                  >
                    {isConfirmingReset ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting…
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-navy mb-2">Password Reset Complete</h2>
                <p className="text-sm text-slate-500 mb-6">{resetConfirmSuccess}</p>
                <button
                  onClick={() => navigate({ to: '/' })}
                  className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Go to Sign In
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} UBSafety — All rights reserved
        </p>
      </div>
    </div>
  )
}
